import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { TableCell, TableRow } from './Table.styles';
import clsx from 'clsx';
import { getAlignmentClass } from './utils';
import { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface SubRowsProps<T extends object, P extends object = T> {
  columns: ColumnDef<T>[];
  row: Row<P>;
  subRowsName: string;
  handleRowClick?: (row: Row<T>) => void;
  isLastRow: boolean;
}

const SubRows = <T extends object, P extends object = T>({
  columns,
  row,
  subRowsName,
  handleRowClick,
  isLastRow,
}: SubRowsProps<T, P>) => {
  const data = useMemo(
    () => ((row.original as Record<string, unknown>)[subRowsName] ?? []) as T[],
    [subRowsName, row],
  );
  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <AnimatePresence>
        <motion.div
          className={clsx(
            'grid-rows-[minmax(48px, 48px)] col-span-full grid auto-rows-[minmax(48px,1fr)] grid-cols-[subgrid] gap-x-4 overflow-hidden border-stroke-base-default',
            {
              'rounded-b': isLastRow,
            },
          )}
          layout
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: row.getIsExpanded() ? (data.length ?? 1) * 48 : 0,
            opacity: row.getIsExpanded() ? 1 : 0,
            borderWidth: row.getIsExpanded()
              ? isLastRow
                ? '0px 1px 1px 1px'
                : '1px 1px 0 1px'
              : '0px',
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          {table.getRowModel().rows.map((row, rowIndex) => (
            <TableRow
              isSubRow
              key={row.id}
              onClick={() => handleRowClick && handleRowClick(row)}
              role="row"
              className={clsx('!gap-0 !p-0', {
                '!border-none':
                  table.getRowModel().rows.length !== rowIndex + 1,
              })}
            >
              {row.getVisibleCells().map((cell, index) => (
                <TableCell
                  key={cell.id}
                  role="cell"
                  aria-rowindex={index}
                  className={clsx(
                    getAlignmentClass(cell.column.columnDef.meta?.align),
                    'border-r border-stroke-base-default bg-background-table-subrow px-4 first:bg-transparent last:border-none',
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default SubRows;
